import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowUpRight,
  Brain,
  Boxes,
  Smartphone,
  Link2,
  Sparkles,
  Cpu,
  Network,
  Cloud,
  Bot,
  Zap,
  ShieldCheck,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { Reveal } from "@/hooks/useReveal";
import { Magnetic } from "@/components/MagneticButton";
import { CountUp } from "@/components/CountUp";
import { SEO } from "@/components/SEO";
import { IndustriesSection } from "@/components/sections/IndustriesSection";
import { ProcessSection } from "@/components/sections/ProcessSection";
import { TrustSection } from "@/components/sections/TrustSection";
import { ClientsSection } from "@/components/sections/ClientsSection";
import { ClientLogoMarquee } from "@/components/portfolio/ClientLogoMarquee";

const Index = () => {
  const { t } = useI18n();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [hash]);

  const pillars = [
    { key: "ai", Icon: Brain },
    { key: "web", Icon: Boxes },
    { key: "mobile", Icon: Smartphone },
    { key: "chain", Icon: Link2 },
  ] as const;

  const capabilities = [
    { key: "ai", Icon: Cpu },
    { key: "chain", Icon: Link2 },
    { key: "rt", Icon: Zap },
    { key: "mobile", Icon: Smartphone },
    { key: "cloud", Icon: Cloud },
    { key: "agents", Icon: Bot },
  ] as const;

  return (
    <>
      <SEO title={t.nav.home} description={t.home.heroSubtitle} />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--foreground)/0.05),transparent_60%)]" />
          <div className="absolute inset-0 bg-grid-coarse opacity-50 mask-radial-fade" />
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-24 lg:pt-40 pb-24 lg:pb-40">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border hairline bg-foreground/[0.02] px-3 py-1 text-xs uppercase tracking-wider text-muted-foreground">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-foreground/40" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-foreground" />
              </span>
              {t.home.heroBadge}
            </div>
          </Reveal>

          <Reveal delay={120}>
            <h1 className="mt-8 font-display text-5xl sm:text-6xl lg:text-[7.5rem] font-semibold tracking-tighter leading-[0.92] text-balance max-w-6xl">
              {t.home.heroTitle}
            </h1>
          </Reveal>

          <Reveal delay={240}>
            <p className="mt-10 max-w-2xl text-lg lg:text-xl text-muted-foreground leading-relaxed text-pretty">
              {t.home.heroSubtitle}
            </p>
          </Reveal>

          <Reveal delay={360}>
            <div className="mt-12 flex flex-wrap items-center gap-3">
              <Link to="/contact">
                <Magnetic>
                  <span className="group inline-flex h-12 items-center gap-2 rounded-full bg-foreground px-6 text-sm font-medium text-background transition-opacity hover:opacity-90">
                    {t.cta.contact}
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Magnetic>
              </Link>
              <Link
                to="/#clients"
                onClick={(e) => {
                  const el = document.getElementById("clients");
                  if (el) {
                    e.preventDefault();
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }}
                className="inline-flex h-12 items-center gap-2 rounded-full border hairline px-6 text-sm font-medium hover:bg-foreground/[0.04] transition-colors"
              >
                {t.cta.explore}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>

          {/* Hero meta strip */}
          <Reveal delay={480}>
            <div className="mt-20 lg:mt-28 grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border hairline rounded-2xl overflow-hidden">
              {[
                { k: (t.home as any).heroStats.aiNative.k, v: (t.home as any).heroStats.aiNative.v },
                { k: (t.home as any).heroStats.delivery.k, v: (t.home as any).heroStats.delivery.v },
                { k: (t.home as any).heroStats.uptime.k, v: (t.home as any).heroStats.uptime.v },
                { k: (t.home as any).heroStats.presence.k, v: (t.home as any).heroStats.presence.v },
              ].map((m) => (
                <div key={m.k} className="bg-background p-5">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground/70">
                    {m.k}
                  </p>
                  <p className="mt-2 font-display text-lg lg:text-xl font-semibold tracking-tight">
                    {m.v}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CORE CAPABILITIES */}
      <section className="border-t hairline relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-10 mb-16">
            <Reveal className="lg:col-span-5">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">
                — 01 / {t.home.capabilitiesEyebrow}
              </p>
              <h2 className="font-display text-4xl lg:text-6xl font-semibold tracking-tighter">
                {t.home.capabilitiesTitle}
              </h2>
            </Reveal>
            <Reveal delay={120} className="lg:col-span-6 lg:col-start-7 lg:pt-4">
              <p className="text-lg text-muted-foreground leading-relaxed text-pretty">
                {t.home.capabilitiesSubtitle}
              </p>
            </Reveal>
          </div>

          <div className="grid gap-px bg-border border hairline rounded-2xl overflow-hidden sm:grid-cols-2 lg:grid-cols-3">
            {capabilities.map(({ key, Icon }, i) => {
              const c = (t.home.capabilities as Record<string, { t: string; d: string }>)[key];
              return (
                <Reveal key={key} delay={i * 60} className="bg-background">
                  <div className="group relative h-full p-8 lg:p-10 card-lift cursor-default">
                    <div className="flex items-start justify-between mb-10">
                      <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl border hairline bg-foreground/[0.02] transition-colors group-hover:bg-foreground group-hover:border-foreground">
                        <Icon className="h-5 w-5 transition-colors group-hover:text-background" strokeWidth={1.5} />
                      </div>
                      <span className="text-[10px] font-mono text-muted-foreground/60">
                        0{i + 1} / 06
                      </span>
                    </div>
                    <h3 className="font-display text-xl lg:text-2xl font-semibold tracking-tight mb-3">
                      {c.t}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed text-sm lg:text-base">
                      {c.d}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <IndustriesSection index="02" />

      {/* HOW WE WORK */}
      <ProcessSection index="03" />

      {/* WHAT WE DO */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <Reveal>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">— 04 / {t.home.whatTitle}</p>
                <h2 className="font-display text-4xl lg:text-6xl font-semibold tracking-tighter max-w-2xl">
                  {t.home.whatSubtitle}
                </h2>
              </div>
            </Reveal>
          </div>

          <div className="grid gap-px bg-border sm:grid-cols-2 border hairline rounded-2xl overflow-hidden">
            {pillars.map(({ key, Icon }, i) => {
              const p = t.home.pillars[key as keyof typeof t.home.pillars];
              return (
                <Reveal key={key} delay={i * 80} className="bg-background">
                  <div className="group relative h-full p-8 lg:p-10 card-lift cursor-default">
                    <div className="flex items-start justify-between mb-12">
                      <Icon className="h-7 w-7 text-foreground/80" strokeWidth={1.4} />
                      <span className="text-xs font-mono text-muted-foreground/60">0{i + 1}</span>
                    </div>
                    <h3 className="font-display text-2xl lg:text-3xl font-semibold tracking-tight mb-3">
                      {p.t}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">{p.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRUST & SECURITY */}
      <TrustSection index="05" />

      {/* WHY */}
      <section className="border-t hairline bg-foreground/[0.015]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
          <Reveal>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-4">— 06 / {t.home.whyTitle}</p>
            <h2 className="font-display text-4xl lg:text-6xl font-semibold tracking-tighter max-w-3xl">
              {t.home.whySubtitle}
            </h2>
          </Reveal>

          <div className="mt-16 grid gap-12 sm:grid-cols-3">
            {[
              { num: 10, suf: "×", l: t.home.stats.speed.l },
              { num: 99, suf: ".99%", l: t.home.stats.scale.l },
              { num: 7, suf: "", l: t.home.stats.team.l },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="border-t hairline pt-6">
                  <p className="font-display text-6xl lg:text-7xl font-semibold tracking-tighter tabular-nums">
                    <CountUp value={s.num} suffix={s.suf} />
                  </p>
                  <p className="mt-4 text-sm text-muted-foreground max-w-xs">{s.l}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI-NATIVE SOFTWARE ENGINEERING IN 2026 */}
      <section className="relative border-t hairline overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-grid-fine opacity-40 mask-radial-fade" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,hsl(var(--foreground)/0.04),transparent_60%)]" />
        </div>

        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-40">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border hairline bg-background/60 backdrop-blur-md px-3 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">
              <Sparkles className="h-3 w-3" />
              {t.home.aiNativeEyebrow}
            </div>
          </Reveal>

          <Reveal delay={120}>
            <h2 className="mt-8 font-display text-4xl lg:text-7xl font-semibold tracking-tighter max-w-5xl text-balance">
              {t.home.aiNativeTitle}
            </h2>
          </Reveal>

          <div className="mt-16 grid lg:grid-cols-12 gap-12">
            <Reveal delay={120} className="lg:col-span-7">
              <p className="text-xl lg:text-2xl text-muted-foreground leading-relaxed text-pretty">
                {t.home.aiNativeBody}
              </p>

              <ul className="mt-10 space-y-4">
                {(t.home.aiNativePoints as string[]).map((point, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-4 group animate-fade-up"
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    <div className="mt-1 flex-shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-full border hairline bg-background">
                      <span className="text-[10px] font-mono tabular-nums">0{i + 1}</span>
                    </div>
                    <p className="text-base lg:text-lg leading-relaxed">{point}</p>
                  </li>
                ))}
              </ul>

              <Link
                to="/technology"
                className="mt-12 inline-flex items-center gap-2 text-sm font-medium group"
              >
                <Sparkles className="h-4 w-4" />
                <span className="link-underline">{t.cta.learn}</span>
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>

            <Reveal delay={240} className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-px bg-border border hairline rounded-2xl overflow-hidden">
                {Object.entries(t.home.aiNativeMetrics as Record<string, { v: string; l: string }>).map(
                  ([k, m], i) => (
                    <div key={k} className="bg-background p-6 lg:p-8 group hover:bg-foreground/[0.02] transition-colors">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground/60">
                          0{i + 1}
                        </span>
                        {i === 0 && <Zap className="h-3.5 w-3.5 text-muted-foreground/60" />}
                        {i === 1 && <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground/60" />}
                        {i === 2 && <Brain className="h-3.5 w-3.5 text-muted-foreground/60" />}
                        {i === 3 && <Network className="h-3.5 w-3.5 text-muted-foreground/60" />}
                      </div>
                      <p className="font-display text-3xl lg:text-4xl font-semibold tracking-tighter tabular-nums">
                        {m.v}
                      </p>
                      <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{m.l}</p>
                    </div>
                  ),
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CLIENTS */}
      <div id="clients">
        <ClientsSection index="07" />
        <ClientLogoMarquee />
      </div>

      {/* FINAL CTA */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-semibold tracking-tighter max-w-4xl mx-auto text-balance">
              {t.home.finalCtaTitle}
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl mx-auto">
              {t.home.finalCtaBody}
            </p>
          </Reveal>
          <Reveal delay={240}>
            <Link to="/contact" className="inline-block mt-10">
              <Magnetic>
                <span className="inline-flex h-14 items-center gap-2 rounded-full bg-foreground px-8 text-base font-medium text-background hover:opacity-90 transition-opacity">
                  {t.cta.start}
                  <ArrowUpRight className="h-5 w-5" />
                </span>
              </Magnetic>
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Index;
